import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getRequiredScreenshotWidth,
  getTierMaxWidth,
  pickScreenshotTier,
} from 'utils/screenshotTiers';

/**
 * Pick and upgrade screenshot tier based on layout size, zoom, and hover.
 * Preloads higher tiers via Image() and swaps without flashing.
 * Hover-driven upgrades are gated by enter-dwell upstream so cursor pass-overs
 * do not request the HQ tier; this hook does not prefetch hover tiers while idle.
 */
export function useAdaptiveScreenshotSrc({
  tiers,
  layoutWidth,
  viewZoom = 1,
  isHovered = false,
  visible = true,
  nativeWidth,
  enabled = true,
  onTierUpgrade,
}) {
  const [activeSrc, setActiveSrc] = useState(null);
  const [activeTier, setActiveTier] = useState(null);
  const loadedTierWidthRef = useRef(0);
  const preloadingRef = useRef(null);

  const requiredWidth = useMemo(
    () =>
      getRequiredScreenshotWidth({
        layoutWidth,
        viewZoom,
        isHovered,
        nativeWidth,
      }),
    [layoutWidth, viewZoom, isHovered, nativeWidth],
  );

  const desired = useMemo(() => {
    if (!enabled || !visible || !tiers?.full) {
      return { tier: null, src: null, maxWidth: 0 };
    }
    return pickScreenshotTier(requiredWidth, tiers, loadedTierWidthRef.current);
  }, [enabled, visible, tiers, requiredWidth]);

  const commitTier = (nextTier, notify = false) => {
    setActiveSrc(nextTier.src);
    setActiveTier(nextTier.tier);
    loadedTierWidthRef.current = Math.max(loadedTierWidthRef.current, nextTier.maxWidth);
    if (notify) onTierUpgrade?.(nextTier);
  };

  useEffect(() => {
    if (!enabled || !visible || !desired.src) return undefined;

    if (!activeSrc) {
      commitTier(desired);
      return undefined;
    }

    if (desired.maxWidth <= loadedTierWidthRef.current) return undefined;
    if (desired.src === activeSrc) return undefined;

    if (preloadingRef.current === desired.src) return undefined;

    preloadingRef.current = desired.src;
    const preload = new Image();
    const targetSrc = desired.src;
    const targetTier = desired;
    preload.onload = () => {
      if (preloadingRef.current !== targetSrc) return;
      commitTier(targetTier, true);
      preloadingRef.current = null;
    };
    preload.onerror = () => {
      if (preloadingRef.current === targetSrc) preloadingRef.current = null;
    };
    preload.src = targetSrc;

    return () => {
      preload.onload = null;
      preload.onerror = null;
      // Allow a later hover to retry if this upgrade was cancelled (e.g. pass-over / leave).
      if (preloadingRef.current === targetSrc) preloadingRef.current = null;
    };
  }, [enabled, visible, desired, activeSrc, onTierUpgrade]);

  useEffect(() => {
    loadedTierWidthRef.current = 0;
    preloadingRef.current = null;
    setActiveSrc(null);
    setActiveTier(null);
  }, [tiers?.full, tiers?.thumb, tiers?.medium]);

  const onImageLoad = event => {
    const naturalWidth = event?.currentTarget?.naturalWidth || 0;
    if (naturalWidth > 0 && activeTier) {
      loadedTierWidthRef.current = Math.max(
        loadedTierWidthRef.current,
        getTierMaxWidth(activeTier),
        naturalWidth,
      );
    }
  };

  return {
    src: visible && enabled ? activeSrc : null,
    tier: activeTier,
    requiredWidth,
    isUpgrading: Boolean(preloadingRef.current),
    onImageLoad,
  };
}
